import { Fragment, type CSSProperties, useMemo } from "react";
import { CalendarPlus, Clock, TimerOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Notify } from "@/components/ui/notify";
import { useLineCalendar } from "@/features/lines/calendar/hooks";
import { useOrgTimezone } from "@/hooks/useOrgTimezone";
import { useProductionOrders } from "./hooks";
import { ExpedienteBands } from "./ExpedienteBands";
import { OP_STATUS_BLOCK } from "./production-order-utils";
import {
  isOverrun,
  outOfWindowBandsPct,
  overrunGeometry,
  overrunLabel,
} from "./production-timeline-geometry";
import { blockRangeGeometryPct, buildRange, localIsoDay, rangeBoundsIso } from "./timeline-range";

// Mini-gantt de ocupação da linha dentro do modal de OP. Mostra as OPs
// existentes da linha NO DIA da janela proposta (interseção real com o dia LOCAL — não slice UTC) +
// a janela proposta (tracejada) e avisa sobreposição. Eixo 00–24h via uma TimelineRange de 1 dia,
// então geometria/bounds são os mesmos da timeline principal (robusto a fuso, sem sliver fantasma).
const TICKS = [0, 4, 8, 12, 16, 20];
const overlaps = (aS: number, aE: number, bS: number, bE: number) => aS < bE && bS < aE;
const num3 = (code: string | null) => (code ?? "").replace(/\D/g, "").slice(-3);

interface Props {
  lineId: string;
  lineName?: string;
  start: string;
  end: string;
  excludeOrderId?: string;
}

export function OpLinePreview({ lineId, lineName, start, end, excludeOrderId }: Props) {
  // Dia LOCAL da janela proposta (a partir dos componentes locais de `start`, não do slice UTC);
  // sem janela ainda, ancora em hoje só p/ a faixa do mini-gantt ter um dia válido.
  const day = useMemo(() => localIsoDay(start ? new Date(start) : new Date()), [start]);
  const range = useMemo(() => buildRange("day", day), [day]);
  // Snapshot do "agora" para detecção de overrun (modal de vida curta — sem tick vivo).
  const now = useMemo(() => new Date(), []);
  const { from, to } = useMemo(() => rangeBoundsIso(range), [range]);
  // overlap=true + bounds do dia LOCAL: traz toda OP que SOBREPÕE o dia (inclui multi-dia que começa
  // na véspera e entra no dia — S-3), não só as que começam nele. Janela cobre o dia inteiro.
  const linePreviewQuery = useProductionOrders({ lineId, from, to, overlap: true, limit: 200 });
  const { data, isError, error, refetch } = linePreviewQuery;
  // Faixas fora do expediente da linha no dia da janela proposta (24/7 ⇒ sem faixas).
  const { data: calendar } = useLineCalendar(lineId);
  const timezone = useOrgTimezone();
  const offHoursBands = useMemo(
    () => outOfWindowBandsPct(calendar, range, timezone),
    [calendar, range, timezone]
  );
  // useMemo p/ estabilizar a referência (senão `data?.items ?? []` recria o array a cada render e
  // invalidaria o memo abaixo a cada keystroke — E-3).
  const items = useMemo(() => data?.items ?? [], [data]);

  const { existing, conflictCode, overrunCode, proposed } = useMemo(() => {
    const hasWindow = !!start && !!end && new Date(end).getTime() > new Date(start).getTime();
    // Interseção REAL com a janela do dia (range.startMs/endMs), não igualdade de slice UTC.
    const ex = items.filter(
      (o) =>
        o.status !== "CANCELLED" &&
        o.id !== excludeOrderId &&
        overlaps(
          new Date(o.plannedStartAt).getTime(),
          new Date(o.expectedEnd).getTime(),
          range.startMs,
          range.endMs
        )
    );
    // O backend bloqueia (LINE_OVERRUN) agendar numa linha com produção em overrun. A run
    // em overrun pode estar FORA do dia ancorado (janela no passado), então não apareceria na barra:
    // avisamos por texto a partir de `items` (que sempre traz toda RUNNING da linha) — assim o preview
    // reflete o bloqueio em vez de mostrar a linha como livre.
    const overrun = items.find((o) => o.id !== excludeOrderId && isOverrun(o, now));
    let code: string | null = null;
    if (hasWindow) {
      const ps = new Date(start).getTime();
      const pe = new Date(end).getTime();
      for (const o of ex) {
        if (
          overlaps(ps, pe, new Date(o.plannedStartAt).getTime(), new Date(o.expectedEnd).getTime())
        ) {
          code = o.code ?? "outra OP";
          break;
        }
      }
    }
    const prop = hasWindow ? blockRangeGeometryPct(start, end, range) : null;
    return {
      existing: ex,
      conflictCode: code,
      overrunCode: overrun ? (overrun.code ?? "uma OP") : null,
      proposed: prop,
    };
  }, [items, start, end, excludeOrderId, range, now]);

  // A checagem de sobreposição não pode virar "sem conflito" quando o GET falha: o form
  // usa este preview para evitar sobrepor OPs, então erro tem que ficar explícito, não silencioso.
  if (isError) {
    return (
      <Notify.QueryError
        error={error}
        onRetry={() => void refetch()}
        title="Não foi possível verificar a ocupação da linha"
      />
    );
  }

  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium text-text-secondary">
          <Clock className="h-3.5 w-3.5" /> Ocupação de {lineName || "linha"}
        </span>
      </div>
      <div className="bg-bg-muted-rd/40 relative h-12 overflow-hidden rounded border">
        <ExpedienteBands bands={offHoursBands} />
        {TICKS.map((h) => (
          <div
            key={h}
            style={{ "--l": `${(h / 24) * 100}%` } as CSSProperties}
            className="border-border-subtle/60 absolute bottom-0 left-[var(--l)] top-0 border-l"
          >
            <span className="absolute left-1 top-0.5 font-mono text-[9px] text-text-tertiary">
              {String(h).padStart(2, "0")}h
            </span>
          </div>
        ))}
        {existing.map((o) => {
          // Mesmo tratamento de overrun do gantt principal: barra esticada até
          // agora + tick do previsto + badge. `now` é um snapshot (modal de vida curta).
          const ov = isOverrun(o, now);
          const og = ov ? overrunGeometry(o.plannedStartAt, o.expectedEnd, now, range) : null;
          const g = og?.bar ?? blockRangeGeometryPct(o.plannedStartAt, o.expectedEnd, range);
          if (!g) return null;
          const b = OP_STATUS_BLOCK[o.status];
          return (
            <Fragment key={o.id}>
              <div
                style={{ "--l": `${g.leftPct}%`, "--w": `${g.widthPct}%` } as CSSProperties}
                className={cn(
                  "absolute bottom-5 left-[var(--l)] top-1 flex w-[var(--w)] items-center gap-0.5 overflow-hidden rounded-sm border border-l-2 px-1",
                  b.bg,
                  b.border,
                  b.accent
                )}
                title={
                  ov
                    ? `${o.code ?? "OP"} · atrasada ${overrunLabel(o.expectedEnd, now)}`
                    : `${o.code ?? ""}`
                }
              >
                <span className={cn("truncate font-mono text-[9px]", b.text)}>
                  #{num3(o.code) || "OP"}
                </span>
                {ov ? (
                  <TimerOff className="ml-auto h-2.5 w-2.5 shrink-0 text-rd-warning-700" />
                ) : null}
              </div>
              {og?.overrun ? (
                <div
                  aria-hidden
                  style={
                    {
                      "--ol": `${og.overrun.leftPct}%`,
                      "--ow": `${og.overrun.widthPct}%`,
                    } as CSSProperties
                  }
                  className="bg-rd-warning-200/40 pointer-events-none absolute bottom-5 left-[var(--ol)] top-1 z-[1] w-[var(--ow)] rounded-r-sm"
                />
              ) : null}
              {og?.plannedEndPct != null ? (
                <div
                  aria-hidden
                  style={{ "--tk": `${og.plannedEndPct}%` } as CSSProperties}
                  className="pointer-events-none absolute bottom-5 left-[var(--tk)] top-1 z-[1] w-0.5 -translate-x-1/2 bg-rd-warning-600"
                />
              ) : null}
            </Fragment>
          );
        })}
        {proposed ? (
          <div
            style={
              { "--l": `${proposed.leftPct}%`, "--w": `${proposed.widthPct}%` } as CSSProperties
            }
            className="absolute bottom-1 left-[var(--l)] top-6 flex w-[var(--w)] items-center gap-1 overflow-hidden rounded-sm border border-dashed border-rd-danger-500 bg-rd-danger-50 px-1"
          >
            <CalendarPlus className="h-2.5 w-2.5 shrink-0 text-rd-danger-600" />
            <span className="truncate font-mono text-[9px] text-rd-danger-700">Nova OP</span>
          </div>
        ) : null}
      </div>
      {overrunCode ? (
        <p className="flex items-center gap-1.5 text-[11px] text-rd-warning-700">
          <TimerOff className="h-3 w-3 shrink-0" /> A linha tem a OP {overrunCode} em execução além
          do previsto — encerre-a antes de agendar.
        </p>
      ) : null}
      {conflictCode ? (
        <p className="flex items-center gap-1.5 text-[11px] text-rd-danger-700">
          <span className="h-1.5 w-1.5 rounded-full bg-rd-danger-500" /> A janela proposta sobrepõe{" "}
          {conflictCode}
        </p>
      ) : overrunCode ? null : (
        <p className="text-[11px] text-text-tertiary">
          Pré-visualização da ocupação da linha no dia da janela.
        </p>
      )}
    </div>
  );
}
